import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, useIntl } from 'react-intl';

import apiCalls from '../utils/apiCalls';
import utils from '../utils/utils';

import GeneralForm from '../components/generalForm';
import { connect } from 'react-redux';
import MultiSelect from '../components/customFields/multiSelect';
import ITEM_COLLECTION_TYPE from '../components/constants/itemCollectionType';
import CustomSelect from '../components/customFields/customSelect';

const MarketplaceAdminItemCollectionForm = ({ match, action, intl, history }) => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [itemCollection, setItemCollection] = useState({});
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (action === 'edit') {
      setError(null);
      setLoading(true);
      apiCalls
        .getItemCollections({
          itemCollectionId: match.params.itemCollectionId,
        })
        .then((result) => {
          setItemCollection(transformItemCollectionForForm(result.data.itemCollections[0]));
          setLoading(false);
        })
        .catch((errors) => {
          setError(utils.getErrorMessageFromResponse(errors));
          setLoading(false);
        });
    }
  }, [match.params.itemCollectionId]);

  useEffect(() => {
    apiCalls
      .getItems({
        marketplaceId: match.params.marketplaceId,
      })
      .then((result) => {
        setItems(result.data.items);
      })
      .catch((errors) => {
        setError(utils.getErrorMessageFromResponse(errors));
      });
  }, []);

  function transformItemCollectionForForm(itemCollection) {
    return {
      ...itemCollection,
      itemIds: itemCollection.items.map((item) => item.id),
    };
  }

  const typeOptions = Object.keys(ITEM_COLLECTION_TYPE).map((key) => {
    const type = ITEM_COLLECTION_TYPE[key];
    return {
      value: type,
      label: intl.formatMessage({ id: 'item.collection.form.field.type.' + type }),
    };
  });

  const { formatMessage } = useIntl();

  const itemOptions = items.map((item) => {
    return {
      value: item.id,
      label: `${utils.getItemName(item, formatMessage)} - #${item.id}`,
    };
  });

  const formSchema = {
    type: 'object',
    required: [],
    properties: {
      type: {
        type: 'string',
        title: intl.formatMessage({ id: 'item.collection.form.field.type.label' }),
        default: 'MARKETPLACE_COLLECTION',
        options: typeOptions,
      },
      marketplaceId: {
        type: 'string',
        default: match.params.marketplaceId,
      },
      name: {
        type: 'string',
        title: intl.formatMessage({ id: 'item.collection.form.field.name.label' }),
      },
      itemIds: {
        type: 'array',
        title: intl.formatMessage({ id: 'item.collection.form.field.items.label' }),
        options: itemOptions,
      },
    },
  };

  const uiSchema = {
    type: {
      'ui:field': 'customSelect',
    },
    marketplaceId: {
      'ui:widget': 'hidden',
    },
    itemIds: {
      'ui:field': 'multiSelect',
    },
  };

  function onFormSubmit() {
    setLoading(true);

    if (action === 'edit') {
      return apiCalls
        .editItemCollection({ itemCollection })
        .then((result) => {
          setLoading(false);
          history.push({
            pathname: `/marketplaceAdmin/${match.params.marketplaceId}/itemCollections`,
          });
        })
        .catch((error) => {
          setLoading(false);
          setError(utils.getErrorMessageFromResponse(error, true));
          window.scrollTo(0, 0);
        });
    } else {
      return apiCalls
        .createItemCollection({ itemCollection })
        .then((result) => {
          setLoading(false);
          history.push({
            pathname: `/marketplaceAdmin/${match.params.marketplaceId}/itemCollections`,
          });
        })
        .catch((error) => {
          setLoading(false);
          setError(utils.getErrorMessageFromResponse(error, true));
          window.scrollTo(0, 0);
        });
    }
  }

  const buttonCopy = action === 'edit' ? 'item.collection.edit.form.submit' : 'item.collection.create.form.submit';

  const formFields = {
    multiSelect: MultiSelect,
    customSelect: CustomSelect,
  };

  const header =
    action === 'edit' && !loading
      ? intl.formatMessage({ id: 'item.collection.edit.header' }, { collection: itemCollection.name })
      : intl.formatMessage({ id: 'item.collection.create.header' });

  return (
    <div className="row">
      <div className="col-12 col-lg-10 offset-lg-1 marketplace-admin__form">
        <GeneralForm
          title={header}
          onChange={(event) => setItemCollection(event.formData)}
          onSubmit={onFormSubmit}
          formSchema={formSchema}
          formUiSchema={uiSchema}
          formFields={formFields}
          formData={itemCollection}
          buttonCopy={buttonCopy}
          errorMessage={error}
          loading={loading}
          hideImageUpload={true}
        />
      </div>
    </div>
  );
};

MarketplaceAdminItemCollectionForm.propTypes = {
  currentUser: PropTypes.object,
  action: PropTypes.string.isRequired,
};

MarketplaceAdminItemCollectionForm.defaultProps = {
  action: 'create',
};

function mapStateToProps(state) {
  return {
    currentUser: state.currentUser.user,
  };
}

export default injectIntl(connect(mapStateToProps)(MarketplaceAdminItemCollectionForm));
